import { useCallback, useEffect, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import {
  getResearch,
  getResearchProgress,
  isError,
  startSubjectResearch,
  type ResearchDocument,
  type ResearchProgress,
  type ResearchSubject,
} from './api';

/**
 * Research, while it is happening and after it has.
 *
 * **The panel exists because "reading up on it" for fifteen minutes is not a progress
 * indicator, it is a promise.** Research used to be one call that either produced a
 * twenty-thousand-word document or produced nothing, and until it landed there was one
 * sentence on the screen that said the same thing whether the pass was thirty seconds from
 * finishing or had died an hour ago.
 *
 * It is now an outline followed by waves of batched per-subject research (F-109), and this
 * shows that: the subjects appear within a minute or two, each with a sentence, and then fill
 * in one wave at a time. Everything on screen is read from a row a pass actually wrote — the
 * overall fraction is computed by the API, and each subject's state is its own row. Nothing
 * here advances on a timer, because a bar that moves for no reason teaches somebody that the
 * bar means nothing.
 */

/**
 * How often to ask, and why it is two numbers.
 *
 * Five seconds while the outline runs, because that is a minute or two of somebody's actual
 * attention and the whole point is watching subjects appear. Thirty once the waves start,
 * because a wave is a Message Batch and batches on this workspace were measured at six to 109
 * minutes — a page polling every five seconds through an hour and a half of that is two
 * thousand requests to watch one number not change.
 */
const FAST_POLL_MS = 5_000;
const SLOW_POLL_MS = 30_000;

/**
 * When to stop asking on our own.
 *
 * Two hours, measured on the clock rather than counted in polls. A count is wrong at one of
 * the two cadences by definition: 240 polls is two hours of waves and twenty minutes of
 * outline, so an outline that hung would give up eight times sooner than the thing this number
 * is supposed to describe.
 *
 * Past two hours a tab has been left open rather than watched, and the push notification is
 * what actually brings somebody back — so the page stops and offers a button, the same shape
 * every other long wait in this app uses.
 */
const MAX_WATCH_MS = 2 * 60 * 60 * 1_000;

/*
 * The price that used to be on this button is gone, and it is not coming back.
 *
 * Leo, 2026-09-15: *"the user should not know about budget, we will hide that in our business
 * model… they will pay a monthly flat fee so budget is our concern."* A learner reading "Go
 * deeper — $0.25" is being asked to do accounting on their own curriculum, per subject, which
 * is a decision they cannot make well and did not sign up to make at all.
 *
 * **What replaces it is scope, not silence.** A subject that will not be researched is still
 * said out loud on this screen, because a plan that is quietly incomplete is worse than one
 * that says which parts of it are not covered. What changed is the reason given: it is what
 * this plan reaches, not what it costs.
 */

/** Subject states, mapped onto the three pill styles that exist. */
const PILL: Record<ResearchSubject['state'], string> = {
  outlined: 'pill pill-pending',
  queued: 'pill pill-pending',
  researching: 'pill pill-pending',
  deep: 'pill',
  // Thin and unfunded are not failures and must not be red: one means there was little to
  // find, the other that the budget stopped before it got there. Both have a button.
  thin: 'pill pill-pending',
  unfunded: 'pill pill-pending',
  failed: 'pill pill-failed',
};

/** Which subjects are worth offering a deeper pass on, and which are already having one. */
const DEEPENABLE: ReadonlySet<ResearchSubject['state']> = new Set([
  'deep',
  'thin',
  'unfunded',
  'failed',
]);

export default function Research({ objectiveId }: { objectiveId: string }) {
  const { t, locale } = useI18n();
  const [documents, setDocuments] = useState<ResearchDocument[] | undefined>();
  const [progress, setProgress] = useState<ResearchProgress | undefined>();
  const [open, setOpen] = useState(false);
  const [deepening, setDeepening] = useState<string | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [stopped, setStopped] = useState(false);
  /**
   * Bumped to send the watcher round again after it has stopped.
   *
   * The poll loop terminates when a run reaches `done`, and its effect depends only on the
   * objective — so pressing "go deeper", which starts a *new* run on the same objective, would
   * otherwise leave the panel frozen on a single reading with no timer anywhere. Same for the
   * "check now" button, which without this is a one-shot refresh dressed as a resume.
   */
  const [watch, setWatch] = useState(0);

  /**
   * Fetches, and deliberately does not write state.
   *
   * **The three outcomes are different and two of them used to look identical.** A failed
   * request is not "the run finished"; an objective nobody has researched is not "the request
   * failed". Collapsing them is how a single offline blip left a running run displayed for
   * ever with no timer and no way back — the caller could not tell that it should reschedule.
   *
   * It returns rather than setting, so the caller can drop the answer if the objective changed
   * while it was in flight. Setting state from in here is the cross-objective leak the sibling
   * effect's own comment exists to warn about.
   */
  const load = useCallback(
    async (): Promise<
      { ok: true; progress: ResearchProgress | undefined } | { ok: false }
    > => {
      const result = await getResearchProgress(objectiveId);
      if (isError(result)) return { ok: false };
      if (!('run' in result) || !result.run) return { ok: true, progress: undefined };
      return { ok: true, progress: result as ResearchProgress };
    },
    [objectiveId],
  );

  useEffect(() => {
    let stale = false;
    void getResearch(objectiveId).then((result) => {
      // Guarded because switching objectives remounts this with a new id while the previous
      // request is still in flight — without it the old objective's research can land on the
      // new one's screen.
      if (stale) return;
      setDocuments(isError(result) ? [] : result.documents);
    });
    return () => {
      stale = true;
    };
  }, [objectiveId, progress?.run.state]);

  /*
   * The poll, and it reschedules itself from the answer rather than running on a fixed timer.
   *
   * A `setInterval` at either speed would be wrong at the other, and switching intervals from
   * a dependency array means a render in the middle of a wave restarts the clock. A chained
   * timeout asks, reads what came back, and decides how long to wait next — which is also what
   * lets it stop cleanly when the run ends rather than firing once more into a finished run.
   */
  useEffect(() => {
    let stale = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const until = Date.now() + MAX_WATCH_MS;
    setStopped(false);

    const tick = async (): Promise<void> => {
      const result = await load();
      // Checked *after* the await and before anything is written: the objective may have
      // changed while this request was in flight, and landing the old one's progress on the new
      // one's screen is exactly the failure the sibling effect guards against.
      if (stale) return;

      if (!result.ok) {
        /*
         * The request failed, which says nothing about the run.
         *
         * Treating this as "finished" is how one 500 or one tunnel leaves a running run on
         * screen for ever with no timer and no button — the "check now" escape is gated on
         * having deliberately stopped, which this is not. So: keep the last good reading, and
         * come round again at the slow cadence.
         */
        timer = setTimeout(() => void tick(), SLOW_POLL_MS);
        return;
      }

      setProgress(result.progress);
      const state = result.progress?.run.state;
      if (state !== 'outlining' && state !== 'deepening') return;
      if (Date.now() >= until) {
        setStopped(true);
        return;
      }
      timer = setTimeout(() => void tick(), state === 'outlining' ? FAST_POLL_MS : SLOW_POLL_MS);
    };

    void tick();
    return () => {
      stale = true;
      if (timer) clearTimeout(timer);
    };
  }, [objectiveId, load, watch]);

  /** Reads once and puts the answer on screen, for the two controls that are not the loop. */
  const refresh = useCallback(async () => {
    const result = await load();
    if (result.ok) setProgress(result.progress);
  }, [load]);

  async function goDeeper(subject: ResearchSubject): Promise<void> {
    if (!window.confirm(t('res.deeper.confirm'))) return;
    setDeepening(subject.subjectId);
    setProblem(undefined);
    const result = await startSubjectResearch(objectiveId, subject.subjectId);
    if (isError(result)) {
      setDeepening(undefined);
      setProblem(explain(result.error));
      // Both refusals mean this screen is arguing with a row it has not read recently.
      // Reloading answers them; retrying against the same stale reading would not.
      void refresh();
      return;
    }
    setDeepening(undefined);
    // A new run on the same objective, so the watcher — which stopped when the last one
    // finished — has to be sent round again. Without this the panel freezes on one reading
    // while a batch the learner just paid for runs invisibly.
    setWatch((round) => round + 1);
  }

  function explain(error: string): string {
    if (error === 'already_running') return t('res.error.running');
    if (error === 'not_ready') return t('res.error.notReady');
    if (error === 'no_subject') return t('res.error.noSubject');
    return t('res.error.generic');
  }

  const run = progress?.run;
  const subjects = progress?.subjects ?? [];
  const working = run?.state === 'outlining' || run?.state === 'deepening';
  const deep = subjects.filter((subject) => subject.state === 'deep').length;

  const when = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <>
      {run && (
        <section className="panel">
          <h2>{t('res.progress.title')}</h2>

          {/* A native `<progress>`, not a div pretending to be one. It carries the value to a
              screen reader without any ARIA of ours, and it cannot drift from the number
              beside it because there is only one number. */}
          <progress value={progress?.overall ?? 0} max={1} />
          <p className="muted small">
            {t(`res.progress.${run.state}` as CopyKey)}
            {subjects.length > 0
              ? ` · ${t('res.progress.of')
                  .replace('{done}', String(deep))
                  .replace('{total}', String(subjects.length))}`
              : ''}
          </p>

          {/* The wait, said out loud rather than hidden behind a spinner. A learner who does
              not know a batch can take an hour concludes after ten minutes that it is broken,
              and the honest answer is that the push is what brings them back. */}
          {run.state === 'deepening' && <p className="muted small">{t('res.progress.slow')}</p>}

          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}

          {/* The same "this page has stopped watching" escape every other long wait gets. */}
          {stopped && working && (
            <div className="actions">
              {/* Resumes the watcher rather than reading once. A "check now" that leaves the
                  page just as unwatched as it was is a button that has to be pressed again
                  every thirty seconds for an hour. */}
              <button type="button" className="button" onClick={() => setWatch((r) => r + 1)}>
                {t('res.progress.check')}
              </button>
            </div>
          )}

          <ul className="gaps">
            {subjects.map((subject) => (
              <li key={subject.subjectId}>
                <p className="meta">
                  <strong>{subject.title}</strong>{' '}
                  <span className={PILL[subject.state]}>
                    {t(`res.state.${subject.state}` as CopyKey)}
                  </span>
                </p>
                <p className="muted small">{subject.abstract}</p>

                {/* One bar per subject, which is the level the work actually happens at: a
                    wave researches six subjects and the overall number moves once, while
                    these six move independently and say which of them is in flight. */}
                <progress value={subjectFraction(subject)} max={1} />

                {subject.characters > 0 && (
                  <p className="muted small">
                    {subject.characters.toLocaleString(locale)} {t('res.characters')}
                  </p>
                )}

                {DEEPENABLE.has(subject.state) && !working && (
                  <div className="actions-inline">
                    {/* Never `primary`. The thing to press on this screen is the fold or the
                        upload; this is the fallback for a section that came back thin. */}
                    <button
                      type="button"
                      className="button"
                      disabled={deepening !== undefined}
                      onClick={() => void goDeeper(subject)}
                    >
                      {deepening === subject.subjectId
                        ? t('res.deeper.working')
                        : t('res.deeper')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {run.error && (
            <p className="problem" role="alert">
              {run.error}
            </p>
          )}
        </section>
      )}

      {documents && documents.length > 0 && (
        <section className="panel">
          <details className="history" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
            <summary>
              <h2>{t('res.title')}</h2>
              <span className="muted small">
                {documents.reduce((total, doc) => total + doc.characters, 0).toLocaleString(locale)}{' '}
                {t('res.characters')}
              </span>
            </summary>

            <p className="muted">{t('res.hint')}</p>

            {documents.map((doc) => (
              <article key={doc.key}>
                <p className="meta small">
                  {doc.title} · {when.format(new Date(doc.producedAt))} · {doc.origin} ·{' '}
                  {doc.characters.toLocaleString(locale)} {t('res.characters')}
                </p>
                {/* Deliberately raw, in a scrolling block, rather than rendered as Markdown.
                    The document's value here is that it is the exact text the curriculum was
                    built from — including the tables and the inline URLs — and a renderer that
                    swallowed a malformed table or a citation would be hiding the thing worth
                    checking. */}
                <pre className="extracted research">
                  {doc.body}
                  {doc.truncated ? `\n\n… ${t('res.truncated')}` : ''}
                </pre>
              </article>
            ))}
          </details>
        </section>
      )}
    </>
  );
}

/**
 * How far through its own life one subject is.
 *
 * **Duplicated from `SUBJECT_PROGRESS` in `@nabu/shared`, deliberately and unhappily.** The
 * shared barrel re-exports `keys.ts`, which imports `node:crypto`; a value import from it
 * would pull that into a browser bundle, and reaching past the barrel into a build artefact
 * would be worse. Every other consumer of these numbers gets the shared table — this one
 * cannot, so it gets a copy with this note on it.
 *
 * The `overall` fraction is *not* duplicated: it comes from the API, computed once, precisely
 * so the browser cannot hold a second opinion about the number that matters most.
 */
function subjectFraction(subject: ResearchSubject): number {
  switch (subject.state) {
    case 'outlined':
      return 0.25;
    case 'queued':
      return 0.4;
    case 'researching':
      return 0.6;
    default:
      return 1;
  }
}
